import React, { useState } from 'react';
import { Box, Button, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { saveAsZip, saveSinglePage, saveAsSinglePdf, saveLargeThumb, saveAsZipPng } from '../utils/pdfUtils.js';

export default function ExportStep({ pdfBytes, pages }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [progress, setProgress] = useState(null);

  const doZip = async () => {
    setBusy(true); setProgress(0); setMsg('Generating ZIP…');
    try {
      await saveAsZip(pdfBytes, pages, (p) => setProgress(p));
      setMsg('ZIP ready.');
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const doSingle = async (idx) => {
    setBusy(true); setMsg(`Exporting ${pages[idx].name}…`);
    try {
      await saveSinglePage(pdfBytes, pages[idx]);
      setMsg('Done.');
    } finally {
      setBusy(false);
    }
  };

  const doMerged = async () => {
    setBusy(true); setProgress(0); setMsg('Recreating PDF…');
    try {
      await saveAsSinglePdf(pdfBytes, pages, (p) => setProgress(p));
      setMsg('PDF ready.');
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const doZipPng = async () => {
    setBusy(true); setProgress(0); setMsg('Generating ZIP of PNGs…');
    try {
      await saveAsZipPng(pdfBytes, pages, (p) => setProgress(p));
      setMsg('ZIP (PNGs) ready.');
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const doLargeThumb = async (idx) => {
    setBusy(true); setMsg(`Exporting ${pages[idx].name} as image…`);
    try {
      await saveLargeThumb(pages[idx]);
      setMsg('Image ready.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="center" alignItems="center" spacing={2}>          
          <Typography variant="subtitle1"><strong>Bulk export</strong></Typography>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="center" alignItems="center" spacing={2}>          
          <Button
            variant="contained"
            onClick={doZip}
            disabled={!pages.length || busy}
          >
            Download ZIP with PDF's
          </Button>
          <Button
            variant="contained"
            onClick={doZipPng}
            disabled={!pages.length || busy}
          >
            Download ZIP with PNG's
          </Button>
          <Button
            variant="contained"
            onClick={doMerged}
            disabled={!pages.length || busy}
          >
            Download merged PDF
          </Button>
        </Stack>

        {busy && (
          <Box sx={{ mt: 2 }}>
            {progress === null ? (
              <LinearProgress />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LinearProgress variant="determinate" value={progress} sx={{ flexGrow: 1 }} />
                <Typography variant="body2" sx={{ minWidth: 48 }}>{progress}%</Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Show the current status message produced via setMsg */}
        {msg && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {msg}
          </Typography>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Download single pages</Typography>
        <Stack spacing={1}>
          {pages.map((p, idx) => (
            <Stack key={p.id} direction="row" spacing={2} alignItems="center">
              <Box component="img" src={p.thumb} alt={p.name} sx={{ width: 56, height: 74, objectFit: 'cover', borderRadius: 1 }} />
              <Typography sx={{ flexGrow: 1 }} noWrap title={p.name}>{p.name}</Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => doSingle(idx)}
                  disabled={busy}
                >
                  Download as PDF
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => doLargeThumb(idx)}
                  disabled={busy}
                >
                  Download as image (PNG)
                </Button>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Paper>
    </Stack>
  );
}
